/**
 * Traduz erros técnicos (Postgres, Stripe, Supabase, rede) em mensagens
 * amigáveis em PT-BR para o usuário. Quando não reconhece o padrão, devolve
 * o `fallback` em vez de vazar stack/JSON cru no toast.
 *
 * Uso:
 *   } catch (err) {
 *     toast.error(friendlyErrorMessage(err, "Não foi possível salvar."));
 *   }
 */
export function friendlyErrorMessage(err: unknown, fallback: string): string {
  if (err === null || err === undefined) return fallback;

  const raw =
    typeof err === "string"
      ? err
      : err instanceof Error
        ? err.message
        : "";

  if (!raw) return fallback;
  const msg = raw.toLowerCase();

  // ─── Rede / conectividade
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network request failed")) {
    return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
  }
  if (msg.includes("timeout") || msg.includes("timed out")) {
    return "A operação demorou demais. Tente novamente em alguns segundos.";
  }

  // ─── Sessão expirada (auth)
  if (msg.includes("sessão expirada") || msg.includes("jwt expired") || msg.includes("invalid jwt")) {
    return "Sua sessão expirou. Entre novamente.";
  }
  if (msg.includes("missing bearer token") || msg.includes("unauthorized")) {
    return "Sessão não encontrada. Entre novamente.";
  }

  // ─── Permissão / RLS
  if (msg.includes("forbidden") || msg.includes("not allowed") || msg.includes("permission denied")) {
    return "Você não tem permissão para essa ação.";
  }
  if (msg.includes("admin only")) {
    return "Apenas administradores podem fazer isso.";
  }

  // ─── Postgres / Supabase
  if (msg.includes("duplicate key") || msg.includes("already exists")) {
    return "Já existe um registro com esses dados.";
  }
  if (msg.includes("violates foreign key")) {
    return "Não foi possível concluir: existem dados dependentes.";
  }
  if (msg.includes("violates not-null") || msg.includes("null value in column")) {
    return "Há campos obrigatórios em branco.";
  }
  if (msg.includes("invalid input value for enum")) {
    return "Um dos valores enviados não é válido.";
  }

  // ─── Stripe
  if (msg.includes("no such subscription") || msg.includes("resource_missing")) {
    return "Assinatura não encontrada no Stripe.";
  }
  if (msg.includes("invalid_request") && msg.includes("stripe")) {
    return "Dados inválidos para o Stripe. Verifique os parâmetros.";
  }
  if (msg.includes("stripe_secret_key not configured")) {
    return "Pagamento indisponível agora. Tente novamente em alguns minutos.";
  }

  // ─── Rate limit / quota
  if (msg.includes("muitas chamadas") || msg.includes("rate limit") || msg.includes("too many")) {
    return "Muitas tentativas em pouco tempo. Aguarde alguns segundos e tente de novo.";
  }
  if (msg.includes("alto volume") || msg.includes("quota")) {
    return "Estamos com alto volume agora. Tente em alguns instantes.";
  }

  // ─── Stack/JSON cru: se contém quebra de linha ou caracteres típicos de stack,
  // não mostra — usa fallback.
  if (raw.includes("\n") || raw.length > 200 || raw.startsWith("{") || raw.includes("at ")) {
    return fallback;
  }

  // ─── Mensagem curta e parece amigável: deixa passar.
  return raw;
}
